import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import moment from 'moment';
import { ADMIN_SCOPES } from '../../constants/admin_scope_constants';
import { AUDIENCES, CATEGORIES, PLATFORMS, CATEGORY_LABELS } from '../../constants/changelog_constants';
import { hasScope } from '../../helpers/scope_helpers';
import { fetchChangelogEntries, deleteChangelogEntry, reorderChangelogEntries, postEntryToSlack } from '../../api/changelog';
import ShopmyImage from '../General/ShopmyImage';
import ChangelogEntryModal from './ChangelogEntryModal';
import ChangelogSettings from './ChangelogSettings';
import LinearIssuesModal from './LinearIssuesModal';
import LinearIcon from './icons/LinearIcon';
import PrIcon from './icons/PrIcon';
import SlackIcon from './icons/SlackIcon';
import './Changelog.scss';

const BANNER_URL = 'https://staging-shopmyshelf-uploads.s3-us-east-2.amazonaws.com/changelog-banner.png';
const DISCLAIMER = 'For internal use only. Do not share externally.';


// Use UTC parsing so MySQL DATE values (returned as `2026-05-12T00:00:00.000Z`)
// don't shift to the previous day in negative-UTC timezones.
const formatDate = rawDate => rawDate ? moment.utc(rawDate).format('MMMM D, YYYY') : '';

const getLoomEmbedUrl = url => {
  if (!url || !url.startsWith('https://www.loom.com/share/')) return null;
  return url.replace('https://www.loom.com/share/', 'https://www.loom.com/embed/');
};

const parseInline = text => {
  const parts = [];
  const regex = /\*\*(.+?)\*\*|\*(.+?)\*|_(.+?)_|(https?:\/\/[^\s<>"]+)/g;
  let last = 0;
  let i = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    if (match[1] !== undefined) parts.push(<strong key={i++}>{match[1]}</strong>);
    else if (match[2] !== undefined) parts.push(<em key={i++}>{match[2]}</em>);
    else if (match[3] !== undefined) parts.push(<em key={i++}>{match[3]}</em>);
    else parts.push(<a key={i++} href={match[4]} target='_blank' rel='noopener noreferrer' className='changelog-text-link'>{match[4]}</a>);
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};

const renderDescription = text => {
  if (!text) return null;
  const lines = text.split('\n');
  const result = [];
  let listItems = [];
  let key = 0;

  const flushList = () => {
    if (listItems.length) {
      result.push(<ul key={key++}>{listItems}</ul>);
      listItems = [];
    }
  };

  for (const line of lines) {
    if (/^[-•] /.test(line)) {
      listItems.push(<li key={key++}>{parseInline(line.slice(2))}</li>);
    } else {
      flushList();
      if (line.trim()) result.push(<p key={key++}>{parseInline(line)}</p>);
      else result.push(<br key={key++} />);
    }
  }
  flushList();
  return result;
};

const sortPublished = list =>
  [...list].sort((a, b) => {
    const dateCmp = a.date === b.date ? 0 : a.date > b.date ? -1 : 1;
    return dateCmp || a.display_order - b.display_order || b.id - a.id;
  });

const SortableEntryRow = ({ entry, onEdit, onDelete, onPostToSlack, slackStatus }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: entry.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const linearId = entry.linear_issue_url?.match(/\/(ENG-\d+)\//)?.[1];
  const prNum = entry.pr_url?.match(/\/pull\/(\d+)/)?.[1];
  const [slackHovered, setSlackHovered] = useState(false);

  const isPosted = !!entry.slacked_at;
  const postedDate = isPosted ? moment(entry.slacked_at).format('MMM D [at] h:mm A') : null;
  const slackLabel = slackStatus === 'loading' ? 'Posting…'
    : slackStatus === 'error' ? 'Failed'
    : isPosted && slackHovered ? `Repost · ${postedDate}`
    : isPosted ? `Posted · ${postedDate}`
    : 'Post to Slack';
  const slackClass = `cl-slack-btn${isPosted ? ' cl-slack-btn--sent' : slackStatus === 'error' ? ' cl-slack-btn--error' : ''}`;

  return (
    <div ref={setNodeRef} style={style} className={`cl-manage-row${isDragging ? ' cl-manage-row--dragging' : ''}`}>
      <div className='cl-drag-handle' {...attributes} {...listeners} title='Drag to reorder'>⠿</div>
      <div className='cl-manage-body'>
        <div className='cl-manage-meta'>
          {(entry.audiences || []).map(aud => (
            <span key={aud} className='changelog-audience-badge'>{aud.charAt(0).toUpperCase() + aud.slice(1)}</span>
          ))}
          {(entry.category || []).map(cat => (
            <span key={cat} className={`changelog-category-badge changelog-category-badge--${cat}`}>
              {CATEGORY_LABELS[cat] || cat}
            </span>
          ))}
          {(entry.platform || []).map(p => (
            <span key={p} className='changelog-platform-badge'>{p.charAt(0).toUpperCase() + p.slice(1)}</span>
          ))}
          {entry.version && <span className='changelog-platform-badge'>v{entry.version}</span>}
          {entry.linear_state && (
            <span className='cl-linear-state-badge'>{entry.linear_state}</span>
          )}
        </div>
        <div className='cl-manage-title'>{entry.title}</div>
        {(linearId || prNum) && (
          <div className='cl-draft-sources'>
            {linearId && (
              <a href={entry.linear_issue_url} target='_blank' rel='noopener noreferrer' className='cl-draft-source-link cl-draft-source-link--linear'>
                <LinearIcon />
                {linearId}
              </a>
            )}
            {prNum && (
              <a href={entry.pr_url} target='_blank' rel='noopener noreferrer' className='cl-draft-source-link cl-draft-source-link--pr'>
                <PrIcon />
                PR #{prNum}
              </a>
            )}
          </div>
        )}
        <div className='cl-manage-desc'>{(entry.description || '').replace(/<[^>]+>/g, '')}</div>
        <div className='cl-manage-actions'>
          <button
            className={slackClass}
            onClick={() => slackStatus !== 'loading' && onPostToSlack(entry.id)}
            disabled={slackStatus === 'loading'}
            onMouseEnter={() => setSlackHovered(true)}
            onMouseLeave={() => setSlackHovered(false)}
          >
            <SlackIcon />
            {slackLabel}
          </button>
          <button className='cl-manage-btn' onClick={() => onEdit(entry)} title='Edit'>✏️</button>
          <button className='cl-manage-btn cl-manage-btn--danger' onClick={() => onDelete(entry)} title='Delete'>🗑</button>
        </div>
      </div>
    </div>
  );
};

const Changelog = ({ currentUser }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [linearModalOpen, setLinearModalOpen] = useState(false);
  const [newDraftIds, setNewDraftIds] = useState(new Set());
  const [editingEntry, setEditingEntry] = useState(null);
  const [activeTab, setActiveTab] = useState('changelog');
  const [statusFilter, setStatusFilter] = useState('published');

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAudience, setFilterAudience] = useState([]);
  const [filterCategory, setFilterCategory] = useState([]);
  const [filterPlatform, setFilterPlatform] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  // Drag-and-drop order for published entries
  const [publishedOrder, setPublishedOrder] = useState([]);

  // Lightbox
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState(null);

  // Loom video modal
  const [loomModal, setLoomModal] = useState(null);

  // Delete confirmation
  const [confirmDeleteEntry, setConfirmDeleteEntry] = useState(null);

  // Slack push state per entry id: 'idle' | 'loading' | 'sent' | 'error'
  const [slackStates, setSlackStates] = useState({});
  const [copiedEntryId, setCopiedEntryId] = useState(null);

  const copyEntryLink = async entryId => {
    const url = `https://admin.shopmyshelf.us/changelog?entry=${entryId}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedEntryId(entryId);
      setTimeout(() => setCopiedEntryId(prev => (prev === entryId ? null : prev)), 2000);
    } catch {
      // Fallback for browsers without async clipboard support
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch {}
      document.body.removeChild(ta);
      setCopiedEntryId(entryId);
      setTimeout(() => setCopiedEntryId(prev => (prev === entryId ? null : prev)), 2000);
    }
  };

  const location = useLocation();
  const targetEntryId = useMemo(() => {
    const id = new URLSearchParams(location.search).get('entry');
    return id ? parseInt(id, 10) : null;
  }, [location.search]);

  const canEdit = hasScope(currentUser?.scopes || [], ADMIN_SCOPES.MANAGE_CHANGELOG);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const loadEntries = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchChangelogEntries('all');
      setEntries(data || []);
    } catch (err) {
      setError('Failed to load entries.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadEntries(); }, []);

  const [highlightedEntryId, setHighlightedEntryId] = useState(null);

  const hasScrolled = React.useRef(false);
  useEffect(() => {
    if (!targetEntryId || loading || !entries.length || hasScrolled.current) return;
    setActiveTab('changelog');
    hasScrolled.current = true;
    setHighlightedEntryId(targetEntryId);
    setTimeout(() => {
      const el = document.querySelector(`[data-entry-id="${targetEntryId}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
    const clearTimer = setTimeout(() => setHighlightedEntryId(null), 60000);
    return () => clearTimeout(clearTimer);
  }, [targetEntryId, loading, entries.length]);

  // Keep publishedOrder in sync with entries
  useEffect(() => {
    setPublishedOrder(sortPublished(entries.filter(e => e.status === 'published')));
  }, [entries]);

  useEffect(() => {
    if (!lightboxImages.length) return;
    const handler = e => {
      if (e.key === 'ArrowRight') setLightboxIndex(i => Math.min(i + 1, lightboxImages.length - 1));
      else if (e.key === 'ArrowLeft') setLightboxIndex(i => Math.max(i - 1, 0));
      else if (e.key === 'Escape') setLightboxImages([]);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxImages]);

  useEffect(() => {
    if (!loomModal) return;
    const handler = e => { if (e.key === 'Escape') setLoomModal(null); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [loomModal]);

  // Published entries sorted for Changelog tab: date primary, display_order secondary
  const publishedEntries = useMemo(() =>
    [...entries.filter(e => e.status === 'published')]
      .sort((a, b) =>
        (a.date === b.date ? 0 : a.date > b.date ? -1 : 1) ||
        a.display_order - b.display_order ||
        b.id - a.id
      ),
    [entries]
  );

  const filteredEntries = useMemo(() => {
    return publishedEntries.filter(entry => {
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const inTitle = (entry.title || '').toLowerCase().includes(q);
        const inDesc = (entry.description || '').replace(/<[^>]+>/g, '').toLowerCase().includes(q);
        if (!inTitle && !inDesc) return false;
      }
      if (filterAudience.length && !filterAudience.some(a => (entry.audiences || []).includes(a))) return false;
      if (filterCategory.length && !filterCategory.some(c => (entry.category || []).includes(c))) return false;
      if (filterPlatform.length && !filterPlatform.some(p => (entry.platform || []).includes(p))) return false;
      return true;
    });
  }, [publishedEntries, searchQuery, filterAudience, filterCategory, filterPlatform]);

  const [draftAudienceFilter, setDraftAudienceFilter] = useState([]);

  const managedEntries = useMemo(() => {
    const filtered = entries.filter(e => e.status === statusFilter);
    if (statusFilter !== 'draft' || !draftAudienceFilter.length) return filtered;
    return filtered.filter(e =>
      draftAudienceFilter.some(a => (e.audiences || []).includes(a))
    );
  }, [entries, statusFilter, draftAudienceFilter]);

  const toggleDraftAudience = a => {
    setDraftAudienceFilter(prev =>
      prev.includes(a) ? prev.filter(v => v !== a) : [...prev, a]
    );
  };

  const groupByDate = list => {
    const groups = [];
    const seen = new Map();
    list.forEach(entry => {
      if (seen.has(entry.date)) {
        seen.get(entry.date).push(entry);
      } else {
        const group = [entry];
        seen.set(entry.date, group);
        groups.push({ date: entry.date, entries: group });
      }
    });
    return groups;
  };

  const toggleFilter = (setter, value) => {
    setter(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
  };

  const hasActiveFilters = !!(searchQuery.trim() || filterAudience.length || filterCategory.length || filterPlatform.length);

  const clearFilters = () => {
    setSearchQuery('');
    setFilterAudience([]);
    setFilterCategory([]);
    setFilterPlatform([]);
  };

  const handlePostToSlack = async id => {
    setSlackStates(prev => ({ ...prev, [id]: 'loading' }));
    try {
      const { slacked_at } = await postEntryToSlack(id);
      setEntries(prev => prev.map(e => e.id === id ? { ...e, slacked_at } : e));
      setSlackStates(prev => ({ ...prev, [id]: 'idle' }));
    } catch {
      setSlackStates(prev => ({ ...prev, [id]: 'error' }));
      setTimeout(() => setSlackStates(prev => ({ ...prev, [id]: 'idle' })), 3000);
    }
  };

  const handleNewEntry = () => { setEditingEntry(null); setModalOpen(true); };
  const handleEdit = entry => { setEditingEntry(entry); setModalOpen(true); };
  const handleDelete = entry => setConfirmDeleteEntry(entry);

  const executeDelete = async () => {
    const entry = confirmDeleteEntry;
    setConfirmDeleteEntry(null);
    try {
      await deleteChangelogEntry(entry.id);
      setEntries(prev => prev.filter(e => e.id !== entry.id));
    } catch {
      window.ALERT?.error('Failed to delete entry.');
    }
  };

  const handleSave = savedEntry => {
    setEntries(prev => {
      const idx = prev.findIndex(e => e.id === savedEntry.id);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = savedEntry;
        return updated;
      }
      return [savedEntry, ...prev];
    });
    setModalOpen(false);
  };

  // Drag-and-drop is now scoped to a single date — drop target must already be in the same date group
  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const activeEntry = publishedOrder.find(e => e.id === active.id);
    const overEntry = publishedOrder.find(e => e.id === over.id);
    if (!activeEntry || !overEntry) return;

    // Compare normalized dates so timezone-suffixed strings still match
    const normalizeDate = d => (d ? String(d).split('T')[0] : '');
    if (normalizeDate(activeEntry.date) !== normalizeDate(overEntry.date)) return;

    const oldIdx = publishedOrder.findIndex(e => e.id === active.id);
    const newIdx = publishedOrder.findIndex(e => e.id === over.id);
    const reordered = arrayMove(publishedOrder, oldIdx, newIdx);
    setPublishedOrder(reordered);

    const updates = reordered.map((e, i) => ({ id: e.id, display_order: i }));
    try {
      await reorderChangelogEntries(updates);
      setEntries(prev => {
        const map = new Map(updates.map(u => [u.id, u.display_order]));
        return prev.map(e => map.has(e.id) ? { ...e, display_order: map.get(e.id) } : e);
      });
    } catch {
      loadEntries();
    }
  };

  // Group published entries by date for the manage view
  const publishedByDate = useMemo(() => {
    const groups = [];
    const map = new Map();
    publishedOrder.forEach(entry => {
      const dateKey = entry.date ? String(entry.date).split('T')[0] : 'no-date';
      if (map.has(dateKey)) {
        map.get(dateKey).entries.push(entry);
      } else {
        const group = { date: dateKey, entries: [entry] };
        map.set(dateKey, group);
        groups.push(group);
      }
    });
    return groups;
  }, [publishedOrder]);

  const openLightbox = (images, index) => {
    setLightboxImages(images);
    setLightboxIndex(index);
  };

  const publishedCount = entries.filter(e => e.status === 'published').length;
  const draftCount = entries.filter(e => e.status === 'draft').length;

  return (
    <div className='changelog-outer'>
      <div className='changelog-inner'>

        {/* Banner */}
        <div className='changelog-banner'>
          <ShopmyImage source={BANNER_URL} alt='The Product Center' className='changelog-banner-img' />
        </div>

        {/* Subtitle */}
        <p className='changelog-subtitle'>{DISCLAIMER}</p>

        {/* Tabs */}
        <div className='changelog-tabs'>
          <button className={`changelog-tab${activeTab === 'changelog' ? ' active' : ''}`} onClick={() => setActiveTab('changelog')}>
            Changelog
          </button>
          {canEdit && (
            <button className={`changelog-tab${activeTab === 'entries' ? ' active' : ''}`} onClick={() => setActiveTab('entries')}>
              Entries
            </button>
          )}
          {canEdit && (
            <button className={`changelog-tab${activeTab === 'settings' ? ' active' : ''}`} onClick={() => setActiveTab('settings')}>
              Settings
            </button>
          )}
        </div>

        {/* ── CHANGELOG TAB ── */}
        {activeTab === 'changelog' && (
          <div>
            {/* Filter bar */}
            <div className='cl-filterbar'>
              <div className='cl-search-wrap'>
                <svg className='cl-search-icon' viewBox='0 0 20 20' fill='none'>
                  <circle cx='8.5' cy='8.5' r='5.5' stroke='currentColor' strokeWidth='1.5'/>
                  <path d='M13 13l3.5 3.5' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round'/>
                </svg>
                <input
                  className='cl-search-input'
                  type='text'
                  placeholder='Search...'
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button className='cl-search-clear' onClick={() => setSearchQuery('')}>×</button>
                )}
              </div>
              <button
                className={`cl-filters-btn${showFilters ? ' active' : ''}`}
                onClick={() => setShowFilters(s => !s)}
              >
                <svg viewBox='0 0 20 20' fill='none' width='14' height='14'>
                  <path d='M3 5h14M6 10h8M9 15h2' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round'/>
                </svg>
                FILTERS
                {(filterAudience.length + filterCategory.length + filterPlatform.length) > 0 && (
                  <span className='cl-filters-count'>
                    {filterAudience.length + filterCategory.length + filterPlatform.length}
                  </span>
                )}
              </button>
              {hasActiveFilters && (
                <button className='cl-filters-clear' onClick={clearFilters}>Clear all</button>
              )}
            </div>

            {showFilters && (
              <div className='cl-filter-panel'>
                <div className='cl-filter-group'>
                  <div className='cl-filter-label'>Audience</div>
                  <div className='cl-filter-pills'>
                    {AUDIENCES.map(a => (
                      <button
                        key={a}
                        className={`cl-filter-pill${filterAudience.includes(a) ? ' active' : ''}`}
                        onClick={() => toggleFilter(setFilterAudience, a)}
                      >
                        {a.charAt(0).toUpperCase() + a.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className='cl-filter-group'>
                  <div className='cl-filter-label'>Type</div>
                  <div className='cl-filter-pills'>
                    {CATEGORIES.map(c => (
                      <button
                        key={c}
                        className={`cl-filter-pill${filterCategory.includes(c) ? ' active' : ''}`}
                        onClick={() => toggleFilter(setFilterCategory, c)}
                      >
                        {CATEGORY_LABELS[c]}
                      </button>
                    ))}
                  </div>
                </div>
                <div className='cl-filter-group'>
                  <div className='cl-filter-label'>Platform</div>
                  <div className='cl-filter-pills'>
                    {PLATFORMS.map(p => (
                      <button
                        key={p}
                        className={`cl-filter-pill${filterPlatform.includes(p) ? ' active' : ''}`}
                        onClick={() => toggleFilter(setFilterPlatform, p)}
                      >
                        {p.charAt(0).toUpperCase() + p.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className='changelog-timeline'>
              {loading && <div className='changelog-loading'>Loading...</div>}
              {error && <div className='changelog-error'>Error: {error}</div>}
              {!loading && !error && filteredEntries.map(entry => {
                const imageAttachments = (entry.attachments || []).filter(u => !u.endsWith('.mov') && !u.endsWith('.mp4'));
                return (
                  <div key={entry.id} data-entry-id={entry.id} className={`changelog-entry-row${highlightedEntryId === entry.id ? ' cl-entry-highlighted' : ''}`}>
                    <div className='changelog-entry-left'>
                      <div className='changelog-entry-date'>{formatDate(entry.date)}</div>
                      {(entry.platform || []).map(p => (
                        <span key={p} className='changelog-platform-text'>
                          {p.charAt(0).toUpperCase() + p.slice(1)}
                          {p === 'mobile' && entry.version ? ` v${entry.version}` : ''}
                        </span>
                      ))}
                      {(entry.audiences || []).map(aud => (
                        <span key={aud} className='changelog-audience-badge'>
                          {aud.charAt(0).toUpperCase() + aud.slice(1)}
                        </span>
                      ))}
                      {(entry.category || []).map(cat => (
                        <span key={cat} className={`changelog-category-badge changelog-category-badge--${cat}`}>
                          {CATEGORY_LABELS[cat] || cat}
                        </span>
                      ))}
                      <button
                        type='button'
                        className={`changelog-copy-link${copiedEntryId === entry.id ? ' copied' : ''}`}
                        onClick={() => copyEntryLink(entry.id)}
                        title='Copy link to this update'
                      >
                        {copiedEntryId === entry.id ? (
                          <span className='changelog-copy-link-label'>Copied</span>
                        ) : (
                          <>
                            <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                              <path d='M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71'/>
                              <path d='M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71'/>
                            </svg>
                            <span className='changelog-copy-link-label'>Copy link</span>
                          </>
                        )}
                      </button>
                    </div>
                    <div className='changelog-entry-content'>
                      <div className='changelog-entry-title'>{entry.title}</div>
                      <div className='changelog-entry-description'>{renderDescription(entry.description)}</div>

                      {imageAttachments.length > 0 && (
                        <div className='changelog-attachment-preview' onClick={() => openLightbox(imageAttachments, 0)}>
                          <ShopmyImage source={imageAttachments[0]} alt='Attachment' className='changelog-attachment-img' />
                          {imageAttachments.length > 1 && (
                            <span className='changelog-attachment-count'>+{imageAttachments.length - 1} more</span>
                          )}
                        </div>
                      )}

                      {entry.loom_url && (
                        <button className='changelog-loom-link' onClick={() => setLoomModal(entry.loom_url)}>
                          Demo
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              {!loading && !error && filteredEntries.length === 0 && (
                <div className='changelog-empty'>
                  {hasActiveFilters ? 'No entries match your filters.' : 'No published entries yet.'}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── ENTRIES TAB ── */}
        {activeTab === 'entries' && canEdit && (
          <div>
            <div className='cl-entries-header'>
              <h2 className='cl-entries-title'>Manage Entries</h2>
              <div className='cl-entries-header-actions'>
                {statusFilter === 'draft' && (
                  <button className='changelog-pull-btn' onClick={() => setLinearModalOpen(true)}>
                    <LinearIcon /> Pull from Linear
                  </button>
                )}
                <button className='changelog-new-btn' onClick={handleNewEntry}>+ New Entry</button>
              </div>
            </div>

            <div className='cl-status-tabs'>
              <button className={`cl-status-tab${statusFilter === 'published' ? ' active' : ''}`} onClick={() => setStatusFilter('published')}>
                Published ({publishedCount})
              </button>
              <button className={`cl-status-tab${statusFilter === 'draft' ? ' active' : ''}`} onClick={() => setStatusFilter('draft')}>
                Drafts ({draftCount})
              </button>
            </div>

            {loading && <div className='changelog-loading'>Loading...</div>}

            {/* Published: grouped by date, drag-and-drop scoped within each date */}
            {!loading && statusFilter === 'published' && publishedByDate.map(group => (
              <div key={group.date} className='cl-manage-group'>
                <div className='cl-manage-date'>{formatDate(group.date)}</div>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={group.entries.map(e => e.id)} strategy={verticalListSortingStrategy}>
                    {group.entries.map(entry => (
                      <SortableEntryRow
                        key={entry.id}
                        entry={entry}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onPostToSlack={handlePostToSlack}
                        slackStatus={slackStates[entry.id] || 'idle'}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              </div>
            ))}

            {/* Drafts: audience filter pills */}
            {!loading && statusFilter === 'draft' && (
              <div className='cl-draft-filterbar'>
                <span className='cl-draft-filter-label'>Filter by audience:</span>
                {AUDIENCES.filter(a => a !== 'admin').map(a => (
                  <button
                    key={a}
                    className={`cl-draft-filter-pill${draftAudienceFilter.includes(a) ? ' active' : ''}`}
                    onClick={() => toggleDraftAudience(a)}
                  >
                    {a.charAt(0).toUpperCase() + a.slice(1)}
                  </button>
                ))}
                {draftAudienceFilter.length > 0 && (
                  <button className='cl-draft-filter-clear' onClick={() => setDraftAudienceFilter([])}>
                    Clear
                  </button>
                )}
              </div>
            )}

            {/* Drafts / All: date-grouped list */}
            {!loading && statusFilter !== 'published' && groupByDate(managedEntries).map(group => (
              <div key={group.date} className='cl-manage-group'>
                <div className='cl-manage-date'>{formatDate(group.date)}</div>
                {group.entries.map(entry => {
                  const linearId = entry.linear_issue_url?.match(/\/(ENG-\d+)\//)?.[1];
                  const prNum = entry.pr_url?.match(/\/pull\/(\d+)/)?.[1];
                  const platforms = entry.platform || [];
                  const isMobile = platforms.includes('mobile');
                  return (
                    <div key={entry.id} className='cl-manage-row'>
                      <div className='cl-manage-body'>
                        <div className='cl-manage-meta'>
                          {newDraftIds.has(entry.id) && <span className='cl-new-badge'>NEW</span>}
                          {(entry.audiences || []).map(aud => (
                            <span key={aud} className='changelog-audience-badge'>{aud.charAt(0).toUpperCase() + aud.slice(1)}</span>
                          ))}
                          {(entry.category || []).map(cat => (
                            <span key={cat} className={`changelog-category-badge changelog-category-badge--${cat}`}>
                              {CATEGORY_LABELS[cat] || cat}
                            </span>
                          ))}
                          {platforms.map(p => (
                            <span key={p} className='changelog-platform-badge'>
                              {p.charAt(0).toUpperCase() + p.slice(1)}
                              {p === 'mobile' && entry.version ? ` · v${entry.version}` : ''}
                            </span>
                          ))}
                          {!isMobile && entry.version && (
                            <span className='changelog-platform-badge'>v{entry.version}</span>
                          )}
                        </div>
                        <div className='cl-manage-drafted-on'>Drafted {formatDate(entry.createdAt)}</div>
                        <div className='cl-manage-title'>{entry.title}</div>
                        {(linearId || prNum) && (
                          <div className='cl-draft-sources'>
                            {linearId && (
                              <a href={entry.linear_issue_url} target='_blank' rel='noopener noreferrer' className='cl-draft-source-link cl-draft-source-link--linear'>
                                <LinearIcon />
                                {linearId}
                              </a>
                            )}
                            {prNum && (
                              <a href={entry.pr_url} target='_blank' rel='noopener noreferrer' className='cl-draft-source-link cl-draft-source-link--pr'>
                                <PrIcon />
                                PR #{prNum}
                              </a>
                            )}
                          </div>
                        )}
                        <div className='cl-manage-desc'>{(entry.description || '').replace(/<[^>]+>/g, '')}</div>
                        <div className='cl-manage-actions'>
                          <button className='cl-manage-btn' onClick={() => handleEdit(entry)} title='Edit'>✏️</button>
                          <button className='cl-manage-btn cl-manage-btn--danger' onClick={() => handleDelete(entry)} title='Delete'>🗑</button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}

            {!loading && managedEntries.length === 0 && (
              <div className='changelog-empty'>No {statusFilter} entries.</div>
            )}
          </div>
        )}

        {/* ── SETTINGS TAB ── */}
        {activeTab === 'settings' && canEdit && <ChangelogSettings />}

      </div>

      {modalOpen && (
        <ChangelogEntryModal
          entry={editingEntry}
          onSave={handleSave}
          onClose={() => setModalOpen(false)}
        />
      )}

      {linearModalOpen && (
        <LinearIssuesModal
          onClose={() => setLinearModalOpen(false)}
          onCreated={result => {
            const { created, failed, createdIds } = result || {};
            window.ALERT?.success?.(`Created ${created} draft${created === 1 ? '' : 's'}.${failed ? ` ${failed} failed.` : ''}`);
            if (Array.isArray(createdIds) && createdIds.length) {
              setNewDraftIds(prev => new Set([...prev, ...createdIds]));
            }
            loadEntries();
          }}
        />
      )}

      {confirmDeleteEntry && (
        <div className='cl-confirm-backdrop' onClick={() => setConfirmDeleteEntry(null)}>
          <div className='cl-confirm-modal' onClick={e => e.stopPropagation()}>
            <div className='cl-confirm-title'>Delete entry?</div>
            <div className='cl-confirm-body'>
              "{confirmDeleteEntry.title}" will be permanently removed and cannot be recovered.
            </div>
            <div className='cl-confirm-actions'>
              <button className='cl-confirm-btn cl-confirm-btn--cancel' onClick={() => setConfirmDeleteEntry(null)}>Cancel</button>
              <button className='cl-confirm-btn cl-confirm-btn--delete' onClick={executeDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {loomModal && (
        <div className='cl-loom-backdrop' onClick={() => setLoomModal(null)}>
          <div className='cl-loom-modal' onClick={e => e.stopPropagation()}>
            <button className='cl-loom-close' onClick={() => setLoomModal(null)}>×</button>
            <div className='cl-loom-embed-wrap'>
              <iframe
                src={getLoomEmbedUrl(loomModal)}
                frameBorder='0'
                allowFullScreen
                title='Loom video'
                className='cl-loom-iframe'
              />
            </div>
          </div>
        </div>
      )}

      {lightboxImages.length > 0 && (
        <div
          className='cl-lightbox-backdrop'
          onClick={() => setLightboxImages([])}
          onTouchStart={e => setTouchStartX(e.touches[0].clientX)}
          onTouchEnd={e => {
            if (touchStartX === null) return;
            const diff = touchStartX - e.changedTouches[0].clientX;
            if (Math.abs(diff) > 50) {
              if (diff > 0) setLightboxIndex(i => Math.min(i + 1, lightboxImages.length - 1));
              else setLightboxIndex(i => Math.max(i - 1, 0));
            }
            setTouchStartX(null);
          }}
        >
          <div className='cl-lightbox' onClick={e => e.stopPropagation()}>
            <button className='cl-lightbox-close' onClick={() => setLightboxImages([])}>×</button>
            {lightboxImages.length > 1 && (
              <button className='cl-lightbox-arrow cl-lightbox-arrow--prev' onClick={() => setLightboxIndex(i => Math.max(i - 1, 0))} disabled={lightboxIndex === 0}>‹</button>
            )}
            <ShopmyImage source={lightboxImages[lightboxIndex]} alt={`Image ${lightboxIndex + 1}`} className='cl-lightbox-img' />
            {lightboxImages.length > 1 && (
              <button className='cl-lightbox-arrow cl-lightbox-arrow--next' onClick={() => setLightboxIndex(i => Math.min(i + 1, lightboxImages.length - 1))} disabled={lightboxIndex === lightboxImages.length - 1}>›</button>
            )}
            {lightboxImages.length > 1 && (
              <div className='cl-lightbox-dots'>
                {lightboxImages.map((_, i) => (
                  <button key={i} className={`cl-lightbox-dot${i === lightboxIndex ? ' active' : ''}`} onClick={() => setLightboxIndex(i)} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = state => ({ currentUser: state.users.currentUser });
export default connect(mapStateToProps)(Changelog);
